import { z } from 'zod'

/**
 * The tree-work job types a pin can carry. Mirrors `JobType` in
 * prisma/schema.prisma, and the "Job details legend" on the printed site maps.
 */
export const JOB_TYPES = [
  'CROWN_CLEANING',
  'CROWN_RAISE',
  'CROWN_RESTORATION',
  'CROWN_THIN',
  'STRUCTURAL_CLEARANCE',
  'OTHER',
] as const

export const jobTypeSchema = z.enum(JOB_TYPES)

export type JobType = z.infer<typeof jobTypeSchema>

/**
 * The work a client may decline, as opposed to the work they may not.
 *
 * OTHER is access, setup and clearing up: what has to happen for the crew to
 * be on the property at all. Letting it be toggled off would let somebody
 * assemble a job nobody can actually turn up to do, so it is also the floor
 * under a proposal most of whose work has been declined.
 */
export type DeclinableJobType = Exclude<JobType, 'OTHER'>

export function isDeclinableJobType(
  type: JobType,
): type is DeclinableJobType {
  return type !== 'OTHER'
}

/**
 * Whether a job type is graded 1-3.
 *
 * OTHER is not. Printing "Other L1" would grade a bucket that has no tiers.
 * Today that is the same exclusion as `isDeclinableJobType`, because OTHER is
 * the one type that is neither graded nor something a client may decline.
 * The two functions stay separate: a later type could be graded and still
 * not something a client may decline.
 */
export function jobTypeHasLevel(type: JobType): type is DeclinableJobType {
  return isDeclinableJobType(type)
}

export const JOB_TYPE_LABELS: Record<JobType, string> = {
  CROWN_CLEANING: 'Crown Cleaning',
  CROWN_RAISE: 'Crown Raise',
  CROWN_RESTORATION: 'Crown Restoration',
  CROWN_THIN: 'Crown Thin',
  STRUCTURAL_CLEARANCE: 'Structural Clearance',
  OTHER: 'Other',
}

/**
 * Effort/severity tier for a job.
 *
 * A fixed ladder of three rather than an open integer: crews price against
 * these tiers, and an unbounded number invites a "level 7" to be typed in the
 * field that means nothing to anyone downstream. Every badge on the reference
 * maps is Level 1, which is why it is also the default.
 */
export const JOB_LEVELS = [1, 2, 3] as const

/**
 * The ladder the picker offers, as the schema that accepts it.
 *
 * One list. A handwritten union of the same numbers would let the field
 * offer a rung the API then refuses, or the reverse, and nothing would
 * notice until a surveyor picked it.
 */
export const jobLevelSchema = z.literal(JOB_LEVELS)

export type JobLevel = z.infer<typeof jobLevelSchema>

/**
 * Narrow a stored level to the 1-3 the DTOs promise.
 *
 * The column is an Int because levels order for pricing, and Postgres has no
 * declarative check constraint through Prisma, so the bound is enforced by
 * the shared input schema on the way in. A row written by some other path (a
 * backfill, a manual fix) could still hold a 7. Clamping a real number is the
 * honest response: a value that contradicts the DTO's type would make the web
 * app, which validates responses against the same schema, reject the whole
 * payload over one bad row.
 *
 * A non-finite number is not a stored integer. Mapping it to the middle rung
 * would print a level nobody chose, so it is refused instead of clamped.
 */
export function toJobLevel(level: number): JobLevel {
  if (!Number.isFinite(level)) {
    throw new Error(
      `Job level must be a finite number, received ${String(level)}`,
    )
  }
  const parsed = jobLevelSchema.safeParse(level)
  if (parsed.success) return parsed.data
  const [lowest, between, highest] = JOB_LEVELS
  if (level <= lowest) return lowest
  if (level >= highest) return highest
  // Strictly between the ends, but not itself a rung (a 1.5). The second
  // rung is that answer: on this ladder it is the only one left.
  return between
}
